export type PatternTemplate = "A" | "B" | "C" | "D" | "E" | "F";

export type QuickSubmitFieldType = "select" | "number" | "currency" | "multi-select";

export type QuickSubmitFieldId =
  | "tooth_scope"
  | "tooth_count"
  | "dental_material"
  | "technology_tier"
  | "lasik_eye_count"
  | "included_items"
  | "ivf_bundle"
  | "cycle_components"
  | "cycle_exclusions"
  | "aligner_package"
  | "arch_scope"
  | "wisdom_teeth_count"
  | "sedation_type"
  | "hospital_included"
  | "augmentation_type"
  | "anaesthetic_included"
  | "skin_check_type"
  | "skin_check_tools"
  | "medicare_rebate_claimed"
  | "consult_type"
  | "session_duration"
  | "other_duration"
  | "total_aud_paid"
  | "price_basis"
  | "state_territory";

export type QuickSubmitField = {
  id: QuickSubmitFieldId;
  label: string;
  type: QuickSubmitFieldType;
  required: boolean | "conditional";
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
  fixedValue?: string;
  showWhen?: {
    field: QuickSubmitFieldId;
    values: string[];
  };
};

export type ProcedureQuickSubmitConfig = {
  procedureName: string;
  procedureSlugs: string[];
  patternTemplate: PatternTemplate;
  priceMin: number;
  priceMax: number;
  defaultPriceBasis: string;
  fields: QuickSubmitField[];
};

export type ProcedureQuickSubmitPayload = {
  procedureId?: string;
  procedureSlug: string;
  patternTemplate: PatternTemplate;
  verticalSlug: string;
  fields: Record<string, string | number | string[] | boolean | null | undefined>;
};

export type QuickSubmitValidationResult =
  | { success: true; data: Record<string, string | number | string[] | boolean | null> }
  | { success: false; errors: Record<string, string> };

export const STATE_TERRITORY_OPTIONS = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

const yesNoOptions = ["Yes", "No", "Not sure", "Not applicable"];
const toothScopeOptions = ["One tooth", "Selected teeth", "Upper arch", "Lower arch", "Both arches", "Not sure"];

export const PROCEDURE_QUICK_SUBMIT_CONFIGS: ProcedureQuickSubmitConfig[] = [
  {
    procedureName: "Dental implants",
    procedureSlugs: ["dental-implants", "dental-implants-single", "dental-implants-full"],
    patternTemplate: "B",
    priceMin: 100,
    priceMax: 50000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("tooth_scope", "Tooth scope", toothScopeOptions),
      numberField("tooth_count", "Number of teeth", 1, 32, 1, { field: "tooth_scope", values: ["One tooth", "Selected teeth"] }),
      selectField("dental_material", "Material", ["Titanium", "Zirconia", "Other", "Not sure"]),
      currencyField(100, 50000),
      priceBasisField(["Total paid", "Price per tooth"], "Total paid"),
      stateField(),
    ],
  },
  {
    procedureName: "Veneers",
    procedureSlugs: ["veneers"],
    patternTemplate: "B",
    priceMin: 100,
    priceMax: 50000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("tooth_scope", "Tooth scope", toothScopeOptions),
      numberField("tooth_count", "Number of teeth", 1, 32, 1, { field: "tooth_scope", values: ["One tooth", "Selected teeth"] }),
      selectField("dental_material", "Material", ["Porcelain", "Composite resin", "Other", "Not sure"]),
      currencyField(100, 50000),
      priceBasisField(["Total paid", "Price per tooth"], "Total paid"),
      stateField(),
    ],
  },
  {
    procedureName: "LASIK",
    procedureSlugs: ["lasik", "laser-eye-surgery"],
    patternTemplate: "C",
    priceMin: 500,
    priceMax: 12000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("technology_tier", "Technology level", ["LASIK", "SMILE", "PRK", "TransPRK", "blended vision", "not sure"]),
      selectField("lasik_eye_count", "Eyes treated", ["One eye", "Both eyes", "Not sure"]),
      multiSelectField("included_items", "Included items", ["Consult", "scans", "surgery", "follow-up visits", "enhancement cover", "medication", "Other"], true),
      currencyField(500, 12000),
      priceBasisField(["Total paid", "Price per eye"], "Total paid"),
      stateField(),
    ],
  },
  {
    procedureName: "IVF fresh cycle",
    procedureSlugs: ["ivf-fresh-cycle"],
    patternTemplate: "F",
    priceMin: 500,
    priceMax: 40000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("ivf_bundle", "Cycle package", ["Fresh cycle only", "Fresh with ICSI", "Fresh with transfer", "Fresh with medication", "Full clinic bundle", "Not sure"]),
      multiSelectField("cycle_components", "Cycle includes", ["Consults", "scans", "blood tests", "egg collection", "fertilisation", "embryo transfer", "freezing", "ICSI", "medication", "anaesthetist", "day surgery", "Other"], true),
      multiSelectField("cycle_exclusions", "Cycle extras", ["Medication", "ICSI", "embryo freezing", "genetic testing", "hospital fees", "anaesthetist", "storage", "donor costs", "Other"], false, 8),
      currencyField(500, 40000),
      priceBasisField(["Total paid"], "Total paid", true),
      stateField(),
    ],
  },
  {
    procedureName: "Invisalign",
    procedureSlugs: ["invisalign"],
    patternTemplate: "C",
    priceMin: 500,
    priceMax: 15000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("aligner_package", "Aligner package", ["Express", "Lite", "Moderate", "Comprehensive", "Refinement only", "Not sure"]),
      selectField("arch_scope", "Arch", ["Upper", "Lower", "Both", "Not sure"]),
      multiSelectField("included_items", "Included items", ["Scan", "aligners", "attachments", "refinements", "retainers", "review visits", "Other"], true),
      currencyField(500, 15000),
      priceBasisField(["Total paid"], "Total paid", true),
      stateField(),
    ],
  },
  {
    procedureName: "Wisdom teeth removal",
    procedureSlugs: ["wisdom-teeth-removal"],
    patternTemplate: "D",
    priceMin: 100,
    priceMax: 15000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("wisdom_teeth_count", "Teeth removed", ["One", "Two", "Three", "Four", "Not sure"]),
      selectField("sedation_type", "Sedation", ["Local anaesthetic", "IV sedation", "General anaesthetic", "Not sure"]),
      selectField("hospital_included", "Hospital included", yesNoOptions, "conditional"),
      currencyField(100, 15000),
      priceBasisField(["Total paid"], "Total paid", true),
      stateField(),
    ],
  },
  {
    // Composite bonding is a cosmetic build-up procedure. Inclusions reflect
    // cosmetic workflow items (mock-up, bonding, polishing). Do NOT add
    // whitening here: that is a separate cosmetic procedure with its own slug.
    procedureName: "Composite bonding",
    procedureSlugs: ["composite-bonding"],
    patternTemplate: "B",
    priceMin: 50,
    priceMax: 20000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("tooth_scope", "Tooth scope", toothScopeOptions),
      numberField("tooth_count", "Number of teeth", 1, 32, 1, { field: "tooth_scope", values: ["One tooth", "Selected teeth"] }),
      multiSelectField("included_items", "Included items", ["Consult", "mock-up", "bonding", "polishing", "review visits", "Other"], true),
      currencyField(50, 20000),
      priceBasisField(["Total paid", "Price per tooth"], "Total paid"),
      stateField(),
    ],
  },
  {
    // Dental filling is a restorative procedure. Inclusions reflect restoration
    // workflow items (anaesthesia, filling material). Cosmetic items such as
    // mock-up, bonding (as a checkbox), polishing or whitening do not apply
    // and must not be added here.
    procedureName: "Dental filling",
    procedureSlugs: ["dental-filling"],
    patternTemplate: "B",
    priceMin: 50,
    priceMax: 20000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("tooth_scope", "Tooth scope", toothScopeOptions),
      numberField("tooth_count", "Number of teeth", 1, 32, 1, { field: "tooth_scope", values: ["One tooth", "Selected teeth"] }),
      selectField("dental_material", "Filling material", ["Composite resin", "Amalgam", "Glass ionomer", "Ceramic", "Gold", "Other", "Not sure"]),
      multiSelectField("included_items", "Included items", ["Consult", "anaesthesia", "filling material", "polishing", "review visits", "Other"], true),
      currencyField(50, 20000),
      priceBasisField(["Total paid", "Price per tooth"], "Total paid"),
      stateField(),
    ],
  },
  {
    procedureName: "Breast augmentation",
    procedureSlugs: ["breast-augmentation"],
    patternTemplate: "D",
    priceMin: 1000,
    priceMax: 50000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("augmentation_type", "Implant type", ["Silicone implant", "Saline implant", "Fat transfer", "Implant with lift", "Not sure"]),
      selectField("hospital_included", "Hospital included", yesNoOptions),
      selectField("anaesthetic_included", "Anaesthetic included", yesNoOptions),
      currencyField(1000, 50000),
      priceBasisField(["Total paid"], "Total paid", true),
      stateField(),
    ],
  },
  {
    procedureName: "Skin lesion removal",
    procedureSlugs: ["skin-lesion-removal"],
    patternTemplate: "D",
    priceMin: 50,
    priceMax: 5000,
    defaultPriceBasis: "Total paid",
    fields: [
      selectField("skin_check_type", "Treatment type", ["GP excision", "Specialist excision", "Cryotherapy", "Biopsy", "Not sure"]),
      multiSelectField("skin_check_tools", "Included items", ["Consult", "dermoscopy", "pathology", "biopsy", "treatment", "follow-up", "Not sure", "Other"], false, 5),
      selectField("medicare_rebate_claimed", "Medicare claimed", yesNoOptions, false),
      currencyField(50, 5000),
      priceBasisField(["Total paid"], "Total paid", true),
      stateField(),
    ],
  },
];

export function getProcedureQuickSubmitConfig(procedureSlug: string) {
  return PROCEDURE_QUICK_SUBMIT_CONFIGS.find((config) =>
    config.procedureSlugs.includes(procedureSlug),
  ) ?? null;
}

export function validateProcedureQuickSubmitPayload(
  payload: ProcedureQuickSubmitPayload,
): QuickSubmitValidationResult {
  const config = getProcedureQuickSubmitConfig(payload.procedureSlug);
  const errors: Record<string, string> = {};

  if (!config) {
    return { success: false, errors: { procedureSlug: "This procedure is not available for quick submit yet." } };
  }

  if (payload.patternTemplate !== config.patternTemplate) {
    errors.patternTemplate = "This procedure pattern does not match the quick-submit setup.";
  }

  if (!payload.verticalSlug || payload.verticalSlug.length > 80) {
    errors.verticalSlug = "Missing procedure category.";
  }

  if (!payload.fields || typeof payload.fields !== "object" || Array.isArray(payload.fields)) {
    return { success: false, errors: { fields: "Submission data is missing. Please reload and try again." } };
  }

  const data: Record<string, string | number | string[] | boolean | null> = {};

  for (const field of config.fields) {
    const visible = isFieldVisible(field, payload.fields);
    const value = field.fixedValue ?? payload.fields[field.id];
    const required = isFieldRequired(field, payload.fields);

    if (!visible) {
      data[field.id] = null;
      continue;
    }

    if (field.type === "multi-select") {
      const values = Array.isArray(value) ? value.map(String).filter(Boolean) : [];
      if (required && values.length === 0) errors[field.id] = "Please choose at least one item.";
      if (field.max && values.length > field.max) errors[field.id] = "Please choose fewer items.";
      if (field.options && values.some((item) => !field.options!.includes(item))) errors[field.id] = "Please choose an allowed option.";
      data[field.id] = values.map(normalizeTextValue);
      continue;
    }

    if (value === undefined || value === null || value === "") {
      if (required) errors[field.id] = field.type === "currency" ? "Please enter the price you paid, in AUD." : "Please choose an option.";
      data[field.id] = null;
      continue;
    }

    if (field.type === "currency" || field.type === "number") {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        errors[field.id] = "Please enter numbers only.";
        continue;
      }
      if ((field.min !== undefined && numericValue < field.min) || (field.max !== undefined && numericValue > field.max)) {
        errors[field.id] = "Please enter a value in the allowed range.";
      }
      if (field.step && numericValue % field.step !== 0) {
        errors[field.id] = "Please enter a whole valid amount.";
      }
      data[field.id] = numericValue;
      continue;
    }

    const textValue = normalizeTextValue(String(value));
    if (field.options && !field.options.includes(textValue)) {
      errors[field.id] = "Please choose an allowed option.";
    }
    data[field.id] = textValue;
  }

  data.out_of_pocket_only = true;
  data.unusual_variant_confirmed = true;

  // Bucket-key gate: at least one pattern-specific dimension MUST be present, or
  // the row will derive an empty bucket_key, become an orphan in the truth view,
  // and silently disappear from the variant pricing display.
  const dimensionFields = BUCKET_KEY_DIMENSION_FIELDS_BY_PATTERN[config.patternTemplate] ?? [];
  if (dimensionFields.length > 0) {
    const hasDimension = dimensionFields.some((fieldId) => {
      const value = data[fieldId];
      if (typeof value === "string" && value.trim() !== "") return true;
      if (Array.isArray(value) && value.length > 0) return true;
      if (typeof value === "number" && Number.isFinite(value)) return true;
      return false;
    });
    if (!hasDimension) {
      errors.bucket_key =
        "Please describe the variant (for example material, scope, or session type) so this price can be displayed.";
    }
  }

  return Object.keys(errors).length > 0 ? { success: false, errors } : { success: true, data };
}

// Mirrors derive_variant_price_report_bucket_key() in
// supabase/migrations/00027_variant_price_reports_bucket_key.sql.
// Keep these aligned so submit-time validation reflects the trigger's expectations.
const BUCKET_KEY_DIMENSION_FIELDS_BY_PATTERN: Record<PatternTemplate, QuickSubmitFieldId[]> = {
  A: ["tooth_scope", "dental_material"],
  B: ["dental_material", "arch_scope"],
  C: ["technology_tier", "lasik_eye_count", "aligner_package"],
  D: ["augmentation_type", "sedation_type", "consult_type"],
  E: ["consult_type", "session_duration"],
  F: ["ivf_bundle"],
};

export function isFieldVisible(
  field: QuickSubmitField,
  values: Record<string, unknown>,
) {
  if (!field.showWhen) return true;
  const currentValue = values[field.showWhen.field];
  return field.showWhen.values.includes(String(currentValue ?? ""));
}

export function isFieldRequired(
  field: QuickSubmitField,
  values: Record<string, unknown>,
) {
  if (field.id === "hospital_included" && values.sedation_type === "General anaesthetic") return true;
  if (field.required === true) return true;
  if (field.required === false) return false;
  return isFieldVisible(field, values);
}

export function toDatabaseValue(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function normalizeTextValue(value: string) {
  return value.trim().replace(/\s+/g, " ").slice(0, 100);
}

function selectField(
  id: QuickSubmitFieldId,
  label: string,
  options: string[],
  required: boolean | "conditional" = true,
  fixedValue?: string,
): QuickSubmitField {
  return { id, label, type: "select", required, options, fixedValue };
}

function numberField(
  id: QuickSubmitFieldId,
  label: string,
  min: number,
  max: number,
  step: number,
  showWhen?: QuickSubmitField["showWhen"],
): QuickSubmitField {
  return { id, label, type: "number", required: "conditional", min, max, step, showWhen };
}

function multiSelectField(
  id: QuickSubmitFieldId,
  label: string,
  options: string[],
  required: boolean,
  max?: number,
): QuickSubmitField {
  return { id, label, type: "multi-select", required, options, max };
}

function currencyField(min: number, max: number): QuickSubmitField {
  return { id: "total_aud_paid", label: "Total paid", type: "currency", required: true, min, max, step: 1 };
}

function priceBasisField(options: string[], fixedValue: string, fixed = false): QuickSubmitField {
  return { id: "price_basis", label: "Price type", type: "select", required: true, options, fixedValue: fixed ? fixedValue : undefined };
}

function stateField(): QuickSubmitField {
  return { id: "state_territory", label: "State or territory", type: "select", required: true, options: STATE_TERRITORY_OPTIONS };
}
